import './HSpecialPageView.scss';

import * as tsx from 'vue-tsx-support';
import Vue, { VNode } from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { MetaInfo } from 'vue-meta';
import { HBtn } from '../HBtn';
import { HSpecialPageLayout } from './HSpecialPageLayout';
import { HBreadCrumbs, HBreadCrumbsItem } from './HBreadCrumbs';
import {
  SearchTagCategoryForGF,
  SpecialPage,
  getMediaDerived,
  BusinessStatus,
} from '~/schemes';
import html2text from '~/helpers/html2text';
import { generateHreflang, TOUR_LIST_LINK_OPTIONS } from '~/helpers';

const NO_INDEX_STATUSES: BusinessStatus[] = ['preparing', 'discontinued'];

export interface HSpecialPageViewProps {
  /** 特集ページ */
  specialPage: SpecialPage;
}

export interface HSpecialPageViewEmits {}

export interface HSpecialPageViewScopedSlots {}

export interface SpHeadSettings {
  specialPage: SpecialPage;
  ogUrl: string;
  headAttrs?: MetaInfo['headAttrs'];
}

export function createSpecialPageHead(
  vm: Vue,
  spHeadSettings: SpHeadSettings,
): MetaInfo {
  const { specialPage, ogUrl, headAttrs } = spHeadSettings;
  const ogImage = getMediaDerived('image', specialPage.ogImage);
  const description = html2text(specialPage.metaDescription).replace(/\n/g, '');
  const title = specialPage.title || '';
  const {
    isExcludedSearchEngineIndex,
    availableLanguages,
    space,
  } = specialPage;
  /** 営業状況フラグ */
  const businessStatus =
    space === 'hotel' ? vm.$hotel.current!.businessStatus : null;

  const head: MetaInfo = {
    title,
    meta: [
      { hid: 'og:title', property: 'og:title', content: title },
      {
        hid: 'og:url',
        property: 'og:url',
        content: ogUrl,
      },
    ],
    link: generateHreflang(vm, { availableLanguages }),
  };

  if (ogImage) {
    head.meta!.push({
      hid: 'og:image',
      property: 'og:image',
      content: vm.$res.img(ogImage),
    });
  }
  if (description) {
    head.meta!.push(
      {
        hid: 'description',
        name: 'description',
        content: description,
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: description,
      },
    );
  }
  /**
   * 限定プランなどを載せた特集ページのクローラ対策
   * - 限定公開ページ
   * - 検索エンジンのインデックスを除外するページ
   * - 施設の営業状況フラグが「準備中」または「運営終了」
   */
  if (
    isExcludedSearchEngineIndex ||
    (businessStatus && NO_INDEX_STATUSES.includes(businessStatus))
  ) {
    head.meta!.push({
      hid: 'robots',
      name: 'robots',
      content: 'noindex, nofollow',
    });
  }

  return { ...head, headAttrs };
}

@Component<HSpecialPageViewRef>({
  name: 'HSpecialPageView',
  provide() {
    return {
      specialPageViewRoot: this,
      tourList: TOUR_LIST_LINK_OPTIONS.feature,
    };
  },

  render(): VNode {
    const {
      isSpecial,
      toSpecialPageLabel,
      isShowBreadcrumb,
      specialPage,
    } = this;

    const { hiddenBookingAction, layout, updatedAt } = specialPage;

    return (
      <div
        staticClass="h-special-page-view"
        class={{
          'h-special-page-view--hidden-booking-action': hiddenBookingAction,
        }}>
        {isShowBreadcrumb && <HBreadCrumbs items={this.breadcrumbs} />}
        <HSpecialPageLayout
          layout={layout}
          updatedAt={isSpecial ? updatedAt : undefined}
        />
        {isSpecial && (
          <HBtn
            staticClass="h-special-page-view__action"
            to={`/${this.$language.current}/sp/`}>
            {toSpecialPageLabel}
          </HBtn>
        )}
      </div>
    );
  },
})
export class HSpecialPageViewRef extends Vue implements HSpecialPageViewProps {
  @Prop({ type: Object }) readonly specialPage!: SpecialPage;

  // breadcrumbs: Breadcrumb[] | [] = [];

  get isSpecial(): boolean {
    const { type } = this.specialPage;
    if (!type) return false;
    return type === 'expected1000' || type === 'special';
  }

  get toSpecialPageLabel() {
    return this.$language.current === 'ja'
      ? '特集一覧へ'
      : this.$t('action.exploreMore');
  }

  get searchTagCategories(): SearchTagCategoryForGF[] {
    return this.specialPage.searchTagCategories || [];
  }

  /** パンくずリスト表示のboolean */
  get isShowBreadcrumb() {
    return (
      this.specialPage.hierarchySettings &&
      this.specialPage.hierarchySettings.isBreadcrumb
    );
  }

  // パンくずリストを適正化したものを取得
  get breadcrumbs(): HBreadCrumbsItem[] {
    const chunks = this.specialPage.parents.slice();
    chunks.push({
      slug: this.specialPage.slug,
      title: this.specialPage.title || '',
    });

    return chunks
      .filter((c) => !!c.title)
      .map((parent) => {
        return {
          to: this.getResolvedBreadcrumbSlug(parent.slug),
          title: parent.title,
        };
      });
  }

  // パンくずリストのslugを存在するディレクトリに合わせて修正
  private getResolvedBreadcrumbSlug(slug: string): string {
    const { space, target } = this.specialPage;
    const lang = this.$language.current;
    let link = '';

    /** @comment こういう処理をしている場所他にもあるから共通化してもいいかも */
    if (space === 'global') {
      link = `/${lang}/sp/${slug}`;
    }

    if (space === 'brand') {
      const brand = this.$hotel.brands.find(({ id }) => id === target);
      link = `/${lang}/brands/${brand?.slug}/sp/${slug}`;
    }

    if (space === 'hotel') {
      const hotel = this.$hotel.all.find(({ id }) => id === target);
      link = `/${lang}/hotels/${hotel?.slug}/sp/${slug}`;
    }

    return link;
  }
}
export const HSpecialPageView = tsx
  .ofType<
    HSpecialPageViewProps,
    HSpecialPageViewEmits,
    HSpecialPageViewScopedSlots
  >()
  .convert(HSpecialPageViewRef);
